/**
 * 埼玉県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「いがまんじゅう」は愛知に、「小麦まんじゅう」は栃木に、「ゆず巻き」も栃木に、
 *   「ねぎぬた」と「こんにゃくのみそおでん」は群馬にすでに置いてあるので、すべて外した。
 * - 「つみっこ」は岩手の「ひっつみ」と、手でちぎった生地を煮るという点で絵が重なるので外し、
 *   代わりに栃の葉で包む「つとっこ」を入れた。
 * - 「煮ぼうとう」は公的な出典が「山梨県のほうとうとは異なる」と明記している。
 *   季節の野菜を煮た汁に幅広の麺をゆでずに入れ、汁にとろみをつけるのが特徴。
 * - 「ねじ」は長さ8センチ、幅1センチ、厚さ3ミリという寸法まで出典に書かれている。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：くわいの含め煮・塩あんびん・ねじ・つとっこ・聖天ずし・かてめし・呉汁（店で食べられる品に差し替え）
 * - 行田フライ：名前の由来は一つに決めない（農水省のページが「布来」説・フライパン説・「富よ来い」説を並べており、諸説あるため）
 * - 行田フライ：発祥の店・始まった年は書かない（公式資料にない）
 * - 行田フライ：市内の店の数（農水省・市とも「20店以上」）はすぐ変わる情報なので書かない
 * - すったて（冷汁うどん）：提供店の数・提供期間（5月〜9月など）はすぐ変わる情報なので書かない
 * - 肉汁うどん（武蔵野うどん）：讃岐うどんとの比較は書かない（他県の料理を引き合いに出す必要がないため）
 * - わらじかつ丼：発祥の店の名前・考案者・始まった年は書かない（町のサイトは小鹿野町が発祥とだけ書き、店名は店紹介として出ているので宣伝になる。年は公式で確かめられない）
 * - わらじかつ丼：かつが2枚のる、わらじのように大きい、という形の説明は書かない（公式資料で確かめられなかった）
 * - 豚みそ丼：「元祖」「発祥の店」は書かない（観光協会のページにあるが、店の名乗りで自治体資料の裏付けがない）
 * - 豚みそ丼：始まった年（2008年など）は書かない（店の情報しかない）
 * - 秩父ホルモン：【暫定】公式の出典が見つかっていない。説明は絵の内容に沿った一般的な記述だけにした。出典が見つかるまで仮の文（2026-10-02）
 * - 浦和のうなぎ：店名・老舗の創業年は書かない
 * - 浦和のうなぎ：伝統産業に指定された年は書かない（市のページに年の記載がない）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { saitamaSceneryTour } from './saitamaScenes';
import { SAITAMA_FOOD_HULLS } from './saitamaFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'gyoda-fry',
    level: 1,
    name: '行田フライ',
    shortName: '行田フライ',
    image: '/assets/foods/saitama/01_gyoda_fry.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '小麦粉を水でやわらかく溶いて鉄板に薄く流し、刻んだねぎや豚肉を入れて両面を焼く。名前はフライだが揚げ物ではなく焼き物で、ソースや醤油だれをかけて食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/33_20_saitama.html',
    icon: { label: 'フライ', shape: 'curry-plate', baseColor: '#d9b77a', accentColor: '#5a3a22' },
    reward: {
      unlockScore: 200,
      area: '行田市ほか北埼玉地方',
      headline: '揚げないのにフライ',
      tip: '昭和初期に全盛期を迎えた足袋工場で働く女工に人気となり、売る店が増えて定着した。名前の由来は「布来」「富よ来い」など諸説ある。',
      links: searchLinks('行田市', 'フライ'),
      official: [{ label: '行田市「フライ」', url: 'https://www.city.gyoda.lg.jp/soshiki/kankyokeizaibu/shoko_kanko/gyomu/kanko_joho/gourmet/2350.html' }, { label: '文化庁「100年フード」埼玉県（フライ・ゼリーフライ）', url: 'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_saitama.html' }],
    },
  },
  {
    id: 'soka-senbei',
    level: 2,
    name: '草加せんべい',
    shortName: '草加せんべい',
    image: '/assets/foods/saitama/02_soka_senbei.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '米粉に熱湯を加えて練り、せいろで90分ほど蒸し、石臼でついて冷水にさらす。のしと型抜きを同時に行って丸い生地を作り、天日で2日から3日干す。焙炉で乾かしてから、先端が金属の箸と「押し瓦」というこてで形よく焼き、醤油を塗る。',
    source:
      'https://www.city.soka.saitama.jp/cont/s1403/010/010/020/senbei.html',
    icon: { label: '草加せんべい', shape: 'mochi', baseColor: '#a8702a', accentColor: '#e0c48a' },
    reward: {
      unlockScore: 500,
      area: '草加市',
      headline: '押し瓦で平らに焼く',
      tip: 'はじめは生地に塩を練りこんだもので、醤油が塗られるようになったのは幕末から。草加せんべいの由来として知られる「おせんさん」の話は、昭和になって作られた物語だと草加市自身が書いている。',
      links: searchLinks('草加市', '草加せんべい'),
    },
  },
  {
    id: 'zeri-furai',
    level: 3,
    name: 'ゼリーフライ',
    shortName: 'ゼリーフライ',
    image: '/assets/foods/saitama/03_zeri_furai.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'おからとじゃがいもに小麦粉と卵、人参と玉ねぎを混ぜ、小判形に整えて160度の油で揚げ、ウスターソースと中濃ソースを混ぜた液にくぐらせる。衣がついていないので、見た目はコロッケに似ているが衣がない。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/33_8_saitama.html',
    icon: { label: 'ゼリーフライ', shape: 'dumpling', baseColor: '#7a4a24', accentColor: '#d9b87a' },
    reward: {
      unlockScore: 1000,
      area: '行田市',
      headline: '小判の形だから「銭フライ」',
      tip: 'ルーツは中国東北地方の「野菜まんじゅう」という料理で、日露戦争に従軍した行田市内の「一福茶屋」の店主が考案したと言われる。明治後期には一般的に食べられるようになった。',
      links: searchLinks('行田市', 'ゼリーフライ'),
    },
  },
  {
    id: 'miso-potato',
    level: 4,
    name: 'みそポテト',
    shortName: 'みそポテト',
    image: '/assets/foods/saitama/04_miso_potato.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'じゃがいもを乱切りにして小麦粉と水の衣をつけ、180度で揚げ、味噌・砂糖・酒・みりん・水のたれをかける。収穫したじゃがいものうち小ぶりなものを囲炉裏で焼き、味噌だれをぬって食べたのが始まりだと言われている。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/33_11_saitama.html',
    icon: { label: 'みそポテト', shape: 'dumpling', baseColor: '#d9b45e', accentColor: '#6b3a1a' },
    reward: {
      unlockScore: 1800,
      area: '秩父地方',
      headline: '農作業の合間の軽食',
      tip: '平地の少ない秩父地方では古くから畑作が行われてきた。平成21年の「埼玉B級ご当地グルメ王決定戦inちちぶ」で優勝して知られるようになった。',
      links: searchLinks('秩父市', 'みそポテト'),
    },
  },
  {
    id: 'suttate-udon',
    level: 5,
    name: 'すったて（冷汁うどん）',
    shortName: 'すったて',
    image: '/assets/foods/saitama/05_suttate_udon.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'すり鉢でごまと味噌をすり合わせ、きゅうりや大葉、みょうがなどの夏野菜も加えてすり、最後に冷たい水を注いで混ぜる。これをつけ汁にして、うどんを食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/33_3_saitama.html',
    icon: { label: 'すったて', shape: 'noodle-bowl', baseColor: '#cbbd8c', accentColor: '#5f8a3a' },
    reward: {
      unlockScore: 3000,
      area: '川島町',
      headline: 'すりたてを冷たい汁で',
      tip: '川島町では冷や汁を「すったて」と呼ぶ。具材をすり鉢ですり、すりたてを食べたことが名前の由来。農繁期に手間がかからず栄養もとれる料理として重宝され、ご飯にかける食べ方もある。',
      links: searchLinks('川島町', 'すったてうどん'),
      official: [{ label: '川島町商工会「すったてとは」', url: 'https://www.kawajima.or.jp/suttate/suttate/index.html' }],
    },
  },
  {
    id: 'nikujiru-udon',
    level: 6,
    name: '肉汁うどん（武蔵野うどん）',
    shortName: '肉汁うどん',
    image: '/assets/foods/saitama/06_nikujiru_udon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'ごわごわとした噛みごたえとコシの強さが特徴の武蔵野うどんを、かつお出汁と醤油の濃い味の温かいつゆにつけて食べる。所沢では、つゆに肉を使う肉汁うどんが定着しつつある。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_saitama.html',
    icon: { label: '肉汁うどん', shape: 'noodle-bowl', baseColor: '#b9a07a', accentColor: '#5a3a22' },
    reward: {
      unlockScore: 4500,
      area: '所沢市ほか武蔵野地域',
      headline: 'ごわっと太い麺を温かいつゆで',
      tip: 'かつては正月や盆のふるまいとして手打ちうどんが親しまれた。季節のゆで野菜を「糧（かて）」と呼んで添え、貴重な小麦を節約するため、かさを増やしたといわれる。',
      links: searchLinks('所沢市', '肉汁うどん'),
      official: [{ label: '所沢市まちづくり観光協会「食べる」（所沢のうどん文化）', url: 'https://tokoro-kankou.jp/?page_id=784' }, { label: '農林水産省「うちの郷土料理」埼玉のうどん', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/33_1_saitama.html' }],
    },
  },
  {
    id: 'higashimatsuyama-yakitori',
    level: 7,
    name: '東松山のやきとり',
    shortName: '東松山のやきとり',
    image: '/assets/foods/saitama/07_yakitori.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '豚のカシラ肉と長ねぎを4本の串に交互に刺して焼き、味噌だれをつける。カシラ肉はこめかみから頬にかけての部位で、もともとは食肉としてあまり使われず加工食品の材料になることが多かった。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/33_16_saitama.html',
    icon: { label: '東松山のやきとり', shape: 'dumpling', baseColor: '#8a4a2a', accentColor: '#4c7a2a' },
    reward: {
      unlockScore: 6500,
      area: '東松山市',
      headline: '焼き鳥だが豚',
      tip: '駅の周辺を中心に市内の約50軒の飲食店で出され、北海道や福島県など全国7都市と並んで「日本7大やきとりの街」と称される。地域の工場で働く人の食事として定着した。',
      links: searchLinks('東松山市', '東松山やきとり'),
    },
  },
  {
    id: 'niboto',
    level: 8,
    name: '煮ぼうとう',
    shortName: '煮ぼうとう',
    image: '/assets/foods/saitama/08_niboto.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '季節の野菜を入れて煮た汁に幅広の麺をゆでずに入れ、汁にとろみをつける。入間郡や比企郡では「ひもかわ」「打ち入れ」、秩父では「おっきりこみ」と呼ばれてきた。山梨のほうとうとは異なる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/33_7_saitama.html',
    icon: { label: '煮ぼうとう', shape: 'pot', baseColor: '#e8ddc0', accentColor: '#4c7a35' },
    reward: {
      unlockScore: 9000,
      area: '深谷市',
      headline: '渋沢栄一が好んだ',
      tip: '深谷市では地元出身の実業家である渋沢栄一が愛した料理として知られ、毎年11月11日の命日に「にぼうと会」が催されている。',
      links: searchLinks('深谷市', '煮ぼうとう'),
    },
  },
  {
    id: 'waraji-katsudon',
    level: 9,
    name: 'わらじかつ丼',
    shortName: 'わらじかつ丼',
    image: '/assets/foods/saitama/09_waraji_katsudon.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'かつに甘辛いたれで味をつけ、ご飯にのせた丼。見た目のボリュームがたっぷりで、衣はさくっとした歯ごたえ。いまでは秩父名物として知られる。',
    source:
      'https://ogano-machi.jp/model-course_01/',
    icon: { label: 'わらじかつ', shape: 'rice-bowl', baseColor: '#b5733a', accentColor: '#f3ead8' },
    reward: {
      unlockScore: 12000,
      area: '小鹿野町／秩父市',
      headline: '小鹿野生まれの秩父名物',
      tip: '秩父名物のわらじかつは、小鹿野町が発祥とされる。埼玉県も、秩父名物「わらじかつ」をみそポテトやしゃくし菜漬けと組み合わせて紹介している。',
      links: searchLinks('秩父市', 'わらじかつ丼'),
      official: [{ label: '小鹿野町シティプロモーションサイト「おがのまち」', url: 'https://ogano-machi.jp/model-course_01/' }],
    },
  },
  {
    id: 'butamiso-don',
    level: 10,
    name: '豚みそ丼',
    shortName: '豚みそ丼',
    image: '/assets/foods/saitama/10_butamiso_don.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '秩父で古くから伝わる、味噌に漬けた豚肉を炭火で焼き上げ、ご飯の上にのせた丼。',
    source:
      'https://www.chichibuji.gr.jp/spot/spot-syousai32/',
    icon: { label: '豚みそ丼', shape: 'rice-bowl', baseColor: '#c98a3e', accentColor: '#6b3b1a' },
    reward: {
      unlockScore: 16000,
      area: '秩父市',
      headline: '味噌漬けの豚を炭火で',
      tip: '秩父では、農作業の合間に食べた小昼飯（こぢゅうはん）やそば、ホルモンと並んで、味噌漬けが独自の食文化として生まれた。',
      links: searchLinks('秩父市', '豚みそ丼'),
      official: [{ label: '秩父地域おもてなし観光公社「観光ガイド」', url: 'https://www.chichibu-omotenashi.com/guide/' }],
    },
  },
  {
    id: 'chichibu-horumon',
    level: 11,
    name: '秩父ホルモン',
    shortName: '秩父ホルモン',
    image: '/assets/foods/saitama/11_chichibu_horumon.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '豚の腸や胃などのホルモンを一口大に切り、濃い味噌だれをからめて炭火で焼く。縁に焦げ目がつくまで焼いて、たれごと食べる。',
    icon: { label: 'ホルモン', shape: 'dumpling', baseColor: '#c48a5a', accentColor: '#6b3b1a' },
    reward: {
      unlockScore: 22000,
      area: '秩父市',
      headline: '味噌だれで焼く豚ホルモン',
      tip: '秩父地域の独自の食文化のひとつにホルモンが挙げられている。',
      links: searchLinks('秩父市', '秩父ホルモン'),
    },
  },
  {
    id: 'urawa-unagi',
    level: 12,
    name: '浦和のうなぎ',
    shortName: '浦和のうなぎ',
    image: '/assets/foods/saitama/12_urawa_unagi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '江戸時代の浦和周辺は沼地が多く川魚の多い水郷地帯で、釣りの行楽客に沼地でとれたうなぎを出したのが始まり。地場のうなぎがいなくなった今も、蒲焼の味が受け継がれている。',
    source:
      'https://www.city.saitama.lg.jp/urawa/001/001/06/p118343.html',
    icon: { label: 'うなぎ', shape: 'rice-bowl', baseColor: '#8a4b1c', accentColor: '#1f1a17' },
    reward: {
      unlockScore: 30000,
      area: 'さいたま市浦和区',
      headline: '沼地の町の二百年の蒲焼',
      tip: '江戸時代、見沼田んぼ周辺の村々は鷹場の御用として、とれた鰻を紀州藩主に献上していた。地方でとれて江戸へ送られる鰻は「旅鰻」と呼ばれた。',
      links: searchLinks('さいたま市浦和区', 'うなぎ'),
      official: [{ label: 'さいたま市「さいたま市伝統産業活性化事業」（岩槻の人形・大宮の盆栽・浦和のうなぎ）', url: 'https://www.city.saitama.lg.jp/005/002/010/009/p084529.html' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const saitamaFoodStage: StageDefinition = {
  regionId: 'saitama',
  regionName: '埼玉県',
  mode: 'food',
  title: '埼玉めしマージ',
  theme: {
    primaryColor: '#5a4a7a',
    secondaryColor: '#ece5d6',
    accentColor: '#c07a2a',
    backgroundColor: '#f9f5ec',
  },
  /** 背景ツアー「埼玉めぐり」。軸は季節。5月の長瀞から始めて、3月の吉見百穴で終わる。最後の一枚は静かに扱う。 */
  scenery: {
    tour: saitamaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, SAITAMA_FOOD_HULLS),
};
